import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Loader2, Save, RefreshCw, AlertTriangle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCompiledPrompt, useUpdateSkill, type SkillRegistryRow } from "@/hooks/useSkillDetail";

interface Props {
  skill: SkillRegistryRow;
}

export function IntentTab({ skill }: Props) {
  const { toast } = useToast();
  const update = useUpdateSkill();
  const compiled = useCompiledPrompt(skill.id);

  const initialYaml = useMemo(
    () => (skill.yaml_spec ? JSON.stringify(skill.yaml_spec, null, 2) : "{}"),
    [skill.yaml_spec],
  );
  const [yamlText, setYamlText] = useState(initialYaml);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    setYamlText(initialYaml);
  }, [initialYaml]);

  const onSave = () => {
    let parsed: unknown;
    try {
      parsed = JSON.parse(yamlText);
    } catch (e) {
      setParseError(e instanceof Error ? e.message : String(e));
      return;
    }
    setParseError(null);
    update.mutate(
      { id: skill.id, patch: { yaml_spec: parsed as never } },
      {
        onSuccess: () => {
          toast({ title: "YAML spec atualizado", description: "Recompilando preview…" });
          compiled.refetch();
        },
        onError: (e) => toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" }),
      },
    );
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">YAML spec da intenção</CardTitle>
          <CardDescription>
            Define identidade, restrições e contrato de saída desta skill. Será mesclado com o Prompt-Mãe.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea
            value={yamlText}
            onChange={(e) => setYamlText(e.target.value)}
            rows={22}
            spellCheck={false}
            className="font-mono text-xs"
          />
          {parseError && (
            <div className="flex items-start gap-2 text-xs text-destructive">
              <AlertTriangle className="w-3 h-3 mt-0.5" /> {parseError}
            </div>
          )}
          <div className="flex gap-2">
            <Button onClick={onSave} disabled={update.isPending}>
              {update.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
              Salvar spec
            </Button>
            <Button variant="outline" onClick={() => setYamlText(initialYaml)}>
              <RefreshCw className="w-4 h-4 mr-1" /> Reverter
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            Prompt compilado
            {compiled.isLoading && <Loader2 className="w-3 h-3 animate-spin" />}
          </CardTitle>
          <CardDescription>Gerado por skill-root-template-compile.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          {compiled.data?.warnings && compiled.data.warnings.length > 0 && (
            <div className="space-y-1">
              {compiled.data.warnings.map((w, i) => (
                <Badge key={i} variant="outline" className="text-xs">
                  ⚠ {w}
                </Badge>
              ))}
            </div>
          )}
          <pre className="p-3 bg-muted rounded-md text-xs whitespace-pre-wrap max-h-[460px] overflow-auto">
            {compiled.error
              ? `Erro: ${(compiled.error as Error).message}`
              : compiled.data?.system_prompt ?? "—"}
          </pre>
        </CardContent>
      </Card>
    </div>
  );
}
